import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Copy, Check, ChevronDown, ChevronUp, ExternalLink, Gift } from 'lucide-react';
import { ClubhouseNav } from '../components/clubhouse/ClubhouseNav';
import { API_URL } from '../utils/apiUrl';
import { clubhouseApi, logoutClubhouse } from '../utils/clubhouseApi';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import './MemberLocker.css';

export const MemberLocker = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [perks, setPerks] = useState([]);
  const [sponsorLogos, setSponsorLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedPerk, setExpandedPerk] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('clubhouse_user')) {
      navigate('/clubhouse/login');
      return;
    }
    fetchData();
    fetchUserProfile();
  }, [navigate]);

  const fetchUserProfile = async () => {
    try {
      const response = await clubhouseApi.get(`/api/clubhouse/profile/me`);
      setUserProfile(response.data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  };

  const fetchData = async () => {
    try {
      // Sponsor logos endpoint is public, so we can use plain axios there.
      const [perksRes, logosRes] = await Promise.all([
        clubhouseApi.get(`/api/clubhouse/perks`),
        axios.get(`${API_URL}/api/clubhouse/sponsor-logos`)
      ]);

      setPerks(perksRes.data);
      setSponsorLogos(logosRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        await logoutClubhouse();
        navigate('/clubhouse/login');
      } else {
        setError('Failed to load perks. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logoutClubhouse();
    navigate('/clubhouse/login');
  };

  const handleCopyCode = async (code, perkId) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(perkId);

      // Track code reveal
      try {
        await clubhouseApi.post(`/api/clubhouse/perks/${perkId}/track-reveal`, {});
      } catch (trackErr) {
        console.error('Failed to track code reveal:', trackErr);
      }

      toast({
        title: "Code Copied!",
        description: `"${code}" has been copied to your clipboard.`,
        duration: 3000,
      });
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      toast({
        title: "Copy Failed",
        description: "Please select and copy the code manually.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const toggleDetails = (perkId) => {
    setExpandedPerk(expandedPerk === perkId ? null : perkId);

    // Track view when details are expanded
    if (expandedPerk !== perkId) {
      trackPerkView(perkId);
    }
  };

  // Track when a perk is viewed
  const trackPerkView = async (perkId) => {
    try {
      await clubhouseApi.post(`/api/clubhouse/perks/${perkId}/track-view`, {});
    } catch (err) {
      console.error('Failed to track perk view:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="member-locker-page">
        <div className="locker-loading">
          <div className="loading-spinner"></div>
          <p>Loading your perks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="member-locker-page">
      <Toaster />
      
      {/* Navigation */}
      <ClubhouseNav onLogout={handleLogout} userProfile={userProfile} />
      
      {/* Header */}
      <header className="locker-header">
        <div className="container">
          <div className="header-content">
            <div className="header-title">
              <h1>Member Vault</h1>
              <p>Exclusive perks & offers for members</p>
            </div>
          </div>
        </div>
      </header>

      {/* Intro Section */}
      <section className="locker-intro">
        <p>
          As a Golf Nuts Clubhouse member, you get exclusive access to perks, discounts, 
          and special promotions from our partners. Browse the offers below and enjoy the savings!
        </p>
      </section>

      {/* Error State */}
      {error && (
        <div className="locker-error">
          <p>{error}</p>
          <button onClick={fetchData}>Try Again</button>
        </div>
      )}

      {/* Perks Grid */}
      <section className="perks-section">
        {perks.length === 0 ? (
          <div className="no-perks">
            <Gift size={48} />
            <h3>No Perks Available Yet</h3>
            <p>Check back soon for exclusive member offers!</p>
          </div>
        ) : (
          <div className="perks-grid">
            {perks.map((perk) => (
              <div key={perk.id} className={`perk-card ${perk.isFeatured ? 'featured' : ''}`}>
                {perk.isFeatured && <div className="featured-badge">Featured</div>}
                
                {/* Partner Logo */}
                {perk.partnerLogo && (
                  <div className="perk-logo">
                    <img src={perk.partnerLogo} alt={perk.partnerName || 'Partner'} />
                  </div>
                )}
                
                {/* Content */}
                <div className="perk-content">
                  <h3 className="perk-title">{perk.title}</h3>
                  {perk.description && (
                    <p className="perk-description">{perk.description}</p>
                  )}
                  
                  {/* Code Section */}
                  {perk.code && (
                    <div className="perk-code-section">
                      <span className="code-label">Code:</span>
                      <div className="code-box">
                        <span className="code-value">{perk.code}</span>
                        <button 
                          className="copy-btn"
                          onClick={() => handleCopyCode(perk.code, perk.id)}
                          title="Copy code"
                        >
                          {copiedCode === perk.id ? (
                            <Check size={16} className="copied" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Expiration */}
                  {perk.expirationDate && (
                    <p className="perk-expiration">
                      Expires {formatDate(perk.expirationDate)}
                    </p>
                  )}
                  
                  {/* Details Accordion */}
                  {perk.details && (
                    <div className="perk-details-section">
                      <button 
                        className="details-toggle"
                        onClick={() => toggleDetails(perk.id)}
                      >
                        <span>Details</span>
                        {expandedPerk === perk.id ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </button>
                      {expandedPerk === perk.id && (
                        <div className="details-content">
                          <p>{perk.details}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Terms Link */}
                  {perk.termsUrl && (
                    <a 
                      href={perk.termsUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="terms-link"
                    >
                      Terms & Conditions
                    </a>
                  )}
                </div>
                
                {/* CTA Button */}
                <div className="perk-cta">
                  {perk.ctaUrl ? (
                    <a 
                      href={perk.ctaUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="cta-button"
                      onClick={() => trackPerkView(perk.id)}
                      data-testid={`perk-cta-${perk.id}`}
                    >
                      {perk.ctaLabel || 'SHOP NOW'}
                      <ExternalLink size={16} />
                    </a>
                  ) : (
                    <button 
                      className="cta-button"
                      onClick={() => perk.code && handleCopyCode(perk.code, perk.id)}
                      data-testid={`perk-get-code-${perk.id}`}
                    >
                      {perk.ctaLabel || 'GET CODE'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Sponsor Logo Strip */}
      {sponsorLogos.length > 0 && (
        <section className="sponsor-strip">
          <div className="sponsor-logos">
            {sponsorLogos.map((logo) => (
              logo.linkUrl ? (
                <a 
                  key={logo.id}
                  href={logo.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sponsor-logo"
                  title={logo.name}
                >
                  <img src={logo.logoUrl} alt={logo.name} />
                </a>
              ) : (
                <div key={logo.id} className="sponsor-logo" title={logo.name}>
                  <img src={logo.logoUrl} alt={logo.name} />
                </div>
              )
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default MemberLocker;
